import {
  IronPythonDiagnosticSeverity,
} from './gen/pcf_mapping_service_pb';
import { pollRevitOperation, type RevitOperationProgressSnapshot } from './operationProgressClient';
import { getPcfMappingGrpcClient } from './pcfMappingGrpcClient';
import { useIntegrationDiagnosticsStore } from '../../stores/useIntegrationDiagnosticsStore';

const getClient = getPcfMappingGrpcClient;

export async function validateMappingsGrpc(
  options?: { onProgress?: (progress: RevitOperationProgressSnapshot) => void }
): Promise<{
  success: boolean;
  validationErrors?: string[];
  error?: string;
}> {
  const client = getClient();
  if (!client) {
    useIntegrationDiagnosticsStore.getState().setLastGrpcError('ValidateMappings: gRPC transport is unavailable');
    return { success: false, error: 'gRPC transport is unavailable' };
  }

  try {
    if (options?.onProgress) {
      try {
        const startResponse = await client.startValidateMappings({});
        if (startResponse.isSuccess && startResponse.operationId) {
          const operationResult = await pollRevitOperation(startResponse.operationId, {
            onProgress: options.onProgress,
            parseResult: parseValidateMappingsResultJson,
          });
          if (!operationResult.success || !operationResult.result) {
            const error = operationResult.error || 'ValidateMappings operation failed';
            useIntegrationDiagnosticsStore.getState().setLastGrpcError(`ValidateMappings: ${error}`);
            return { success: false, error };
          }
          return operationResult.result;
        }
      } catch {
        // Older connector builds only expose the unary method.
      }
    }

    const response = await client.validateMappings({});
    if (response.isSuccess) {
      useIntegrationDiagnosticsStore.getState().setLastGrpcError(null);
    } else if (response.error) {
      useIntegrationDiagnosticsStore.getState().setLastGrpcError(`ValidateMappings: ${response.error}`);
    }
    return {
      success: response.isSuccess,
      validationErrors: response.validationErrors || undefined,
      error: response.error || undefined,
    };
  } catch (error) {
    const message = error instanceof Error ? error.message : 'ValidateMappings gRPC error';
    useIntegrationDiagnosticsStore.getState().setLastGrpcError(`ValidateMappings: ${message}`);
    return { success: false, error: message };
  }
}
function parseValidateMappingsResultJson(resultJson: string): {
  success: boolean;
  validationErrors?: string[];
  error?: string;
} {
  const parsed = JSON.parse(resultJson);
  return {
    success: Boolean(parsed.isSuccess),
    validationErrors: Array.isArray(parsed.validationErrors) ? parsed.validationErrors : undefined,
    error: parsed.error || undefined,
  };
}
export interface IronPythonRuntimeDiagnostic {
  line: number;
  column: number;
  message: string;
  severity: 'error' | 'warning';
}
export interface IronPythonRuntimeValidationResult {
  available: boolean;
  success: boolean;
  diagnostics: IronPythonRuntimeDiagnostic[];
  error?: string;
}

export async function validateIronPythonScriptGrpc(
  source: string,
  signal?: AbortSignal
): Promise<IronPythonRuntimeValidationResult> {
  const client = getClient();
  if (!client) {
    return {
      available: false,
      success: false,
      diagnostics: [],
      error: 'IronPython validation is unavailable.',
    };
  }

  try {
    const response = await client.validateIronPythonScript({ source }, { signal });
    return {
      available: true,
      success: response.isSuccess,
      diagnostics: response.diagnostics.map((diagnostic) => ({
        line: diagnostic.line,
        column: diagnostic.column,
        message: diagnostic.message,
        severity: diagnostic.severity === IronPythonDiagnosticSeverity.WARNING
          ? 'warning'
          : 'error',
      })),
      error: response.error || undefined,
    };
  } catch (error) {
    if (signal?.aborted) {
      throw error;
    }
    return {
      available: false,
      success: false,
      diagnostics: [],
      error: error instanceof Error ? error.message : 'IronPython validation is unavailable.',
    };
  }
}
